import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IndividualConfig, ToastrService } from 'ngx-toastr';

import { ComponentType } from '@angular/cdk/portal';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { CipoErrorModel, CipoErrorWithPropModel } from '@models/error/cipo-error.model';
import { CipoExceptionCode } from '@models/module/fields/enums/cipo-exception-code';

export class CipoErrorsAndWarnings {
  errors: string[];
  warnings: string[];

  constructor() {
    this.errors = [];
    this.warnings = [];
  }
}

const NO_PROPERTY: string = '_NoProperty_';
const DEFAULT_ERROR_MESSAGE: string = 'errors.unidentifiedError';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private translate = inject(TranslateService);
  private toastr = inject(ToastrService);
  private snackBar = inject(MatSnackBar);

  private toastrConfig: Partial<IndividualConfig> = {
    timeOut: 5000,
    progressBar: true,
    enableHtml: true,
    toastClass: 'cipo-toast',
    closeButton: true,
  };
  private snackBarConfig: MatSnackBarConfig<unknown> = { horizontalPosition: 'right', verticalPosition: 'top' };

  error(message: string | HttpErrorResponse, interpolateParams?: object) {
    if (message instanceof HttpErrorResponse && message.status === 401) {
      // ignore status 401, it is handled and there is no need to notify it
      return;
    }
    if (typeof message === 'string' && interpolateParams) {
      this.toastr.error(this.translate.instant(message, interpolateParams), '', this.toastrConfig);
      return;
    }
    this.handleHttpErrorResponse(message);
  }

  success(message: string, interpolateParams?: object) {
    this.toastr.success(this.translate.instant(message, interpolateParams), '', this.toastrConfig);
  }

  warning(message: string, interpolateParams?: object) {
    this.toastr.warning(this.translate.instant(message, interpolateParams), '', this.toastrConfig);
  }

  snackBarMessage(message: string, action: string = 'general.ok', config: MatSnackBarConfig<unknown> = {}) {
    return this.snackBar.open(this.translate.instant(message), this.translate.instant(action), {
      ...this.snackBarConfig,
      ...config,
    });
  }

  snackBarMessageFromComponent<T>(component: ComponentType<T>, config: MatSnackBarConfig<unknown> = {}) {
    return this.snackBar.openFromComponent(component, {
      ...this.snackBarConfig,
      ...config,
    });
  }

  getErrorsAndWarnings(response: string | HttpErrorResponse): CipoErrorsAndWarnings | undefined {
    let errorsAndWarnings = new CipoErrorsAndWarnings();

    if (typeof response === 'string') {
      errorsAndWarnings.errors.push(this.formatErrorMessage(response));
      return errorsAndWarnings;
    }

    if (response instanceof HttpErrorResponse) {
      const error = response?.error?.messages;

      if (error && Array.isArray(error) && this.instanceOfCipoErrorModel(error[0])) {
        errorsAndWarnings = this.formatCipoError(error);
      } else if (error && this.instanceOfCipoErrorModel(error)) {
        errorsAndWarnings = this.formatCipoError([error]);
      } else if (error && !Array.isArray(error) && !this.instanceOfCipoErrorModel(error) && !error?.message) {
        errorsAndWarnings = this.formatCipoErrorWithProp(error);
      } else {
        errorsAndWarnings.errors.push(this.formatErrorMessage(response?.message));
      }

      if (errorsAndWarnings?.warnings?.length) {
        errorsAndWarnings.warnings = errorsAndWarnings.warnings.map(w => this.formatErrorMessage(w));
      } else if (errorsAndWarnings?.errors?.length) {
        errorsAndWarnings.errors = errorsAndWarnings.errors.map(e => this.formatErrorMessage(e));
      }
      return errorsAndWarnings;
    }

    return undefined;
  }

  private handleHttpErrorResponse(response: string | HttpErrorResponse): void {
    const errorsAndWarnings = this.getErrorsAndWarnings(response);

    if (!errorsAndWarnings) {
      this.toastr.error(this.translate.instant(DEFAULT_ERROR_MESSAGE), '', this.toastrConfig);
      return;
    }
    if (errorsAndWarnings.warnings.length) {
      this.toastr.warning(errorsAndWarnings.warnings.join('<br/>'), '', this.toastrConfig);
    } else if (errorsAndWarnings.errors.length) {
      this.toastr.error(errorsAndWarnings.errors.join('<br/>'), '', this.toastrConfig);
    }
  }

  private instanceOfCipoErrorModel(obj: unknown): obj is CipoErrorModel {
    return typeof obj === 'object' && obj !== null && 'code' in obj && 'type' in obj && 'message' in obj;
  }

  private formatCipoError(messages: CipoErrorModel[]): CipoErrorsAndWarnings {
    const err = new CipoErrorsAndWarnings();
    err.errors = messages.map(m => this.formatCipoErrorMessage(m));
    return err;
  }

  private formatCipoErrorMessage({ code, message }: CipoErrorModel): string {
    const codeName = typeof code === 'string' ? code : CipoExceptionCode[code];
    const translationKey = typeof codeName === 'string' && codeName ? `errorCode.${codeName}` : '';
    const translatedMessage = translationKey ? this.translate.instant(translationKey) : '';
    const hasTranslation = translatedMessage && translatedMessage !== translationKey;
    const fallbackMessage = message || DEFAULT_ERROR_MESSAGE;

    if (!hasTranslation) {
      return fallbackMessage;
    }
    if (translatedMessage === fallbackMessage) {
      return translatedMessage;
    }
    return `${translatedMessage} ${fallbackMessage}`;
  }

  private formatErrorMessage(msg: string) {
    return this.translate.instant(msg || DEFAULT_ERROR_MESSAGE).replace('\n', '<br/>');
  }

  private formatCipoErrorWithProp(message: CipoErrorWithPropModel): CipoErrorsAndWarnings {
    const err = new CipoErrorsAndWarnings();

    for (const key of Object.keys(message)) {
      for (const item of message[key]) {
        const formatted = this.formatCipoErrorMessage(item);
        const display = key === NO_PROPERTY ? formatted : `${key}: ${formatted}`;
        if (!item.type) {
          err.errors.push(display);
        } else {
          err.warnings.push(display);
        }
      }
    }

    return err;
  }
}
